import type { ReactNode } from 'react';
import { Loader2 } from 'lucide-react';
import { scoreLabel } from '../lib/format';

export function Spinner({ label = 'Loading' }: { label?: string }) {
  return (
    <div role="status" className="flex items-center gap-2 py-8 text-muted">
      <Loader2 className="animate-spin" size={20} aria-hidden />
      <span>{label}…</span>
    </div>
  );
}

export function ErrorNote({ error, children }: { error?: unknown; children?: ReactNode }) {
  const message = children ?? (error instanceof Error ? error.message : 'Something went wrong.');
  return (
    <p role="alert" className="rounded-xl bg-warn-bg px-4 py-3 text-sm text-warn">
      {message}
    </p>
  );
}

export function Empty({ children }: { children: ReactNode }) {
  return <div className="rounded-2xl border border-dashed border-line-2 p-7 text-center text-muted">{children}</div>;
}

export function ScoreBadge({ score, curated }: { score: number; curated?: boolean }) {
  if (curated) {
    return <span className="chip bg-amber/15 font-semibold text-amber-hi">Hand-picked</span>;
  }
  return (
    <span className="chip font-semibold" title={`Backroad score ${score} of 100`}>
      <span className="tabular-nums text-amber">{score}</span>
      <span className="text-muted">{scoreLabel(score)}</span>
    </span>
  );
}

export function Field({
  label,
  htmlFor,
  error,
  hint,
  children,
}: {
  label: string;
  htmlFor: string;
  error?: string[];
  hint?: string;
  children: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={htmlFor}>{label}</label>
      {children}
      {hint && !error?.length && <p className="text-[13px] text-dim">{hint}</p>}
      {error?.map((e) => (
        <p key={e} className="text-[13px] text-danger">
          {e}
        </p>
      ))}
    </div>
  );
}

export function PageHeader({ eyebrow, title, children }: { eyebrow?: string; title: string; children?: ReactNode }) {
  return (
    <div className="mx-auto flex max-w-7xl flex-col gap-3 px-4 pb-6 pt-10 sm:px-6">
      {eyebrow && <p className="eyebrow">{eyebrow}</p>}
      <h1 className="text-5xl font-black sm:text-6xl">{title}</h1>
      {children && <div className="max-w-3xl text-lg text-muted">{children}</div>}
    </div>
  );
}
